import { useCallback, useState } from 'react';


import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import {
  DriverRead,
  DriverTagRead,
  TagRead,
  useDriversRetrieveQuery,
  useDriverTagsCreateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { TagModal } from 'src/common/tags/TagModal';
import formatServerError from 'src/common/util/serverErrorFormatter';
import { teamSlice } from 'src/features/team/team.slice';

import type {
  SelectOption,
  SelectOptionWithColor,
} from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';

export default function DriverTagModal() {
  const dispatch = useAppDispatch();
  const driverTagModal = useAppSelector((state) => state.team.driverTagModal);
  const [error, setError] = useState<string | null>(null);
  const [saveDriverTags] = useDriverTagsCreateMutation();

  const {
    data: selectedDriver,
    isLoading: isDriverLoading,
    isFetching: isDriverFetching,
    error: DriverError,
  } = useDriversRetrieveQuery(
    {
      id: driverTagModal.driverId,
    },
    {
      skip: !driverTagModal.driverId,
    }
  );

  const closeModal = useCallback(() => {
    setError(null);
    dispatch(teamSlice.actions.onCloseDriverTags());
  }, [setError, dispatch]);

  const handleSubmit = useCallback(
    async (selectedTags: SelectOptionWithColor[], allTags: TagRead[]) => {
      try {
        const driverTags = selectOptionsToDriverTags(
          selectedTags,
          allTags,
          selectedDriver
        );

        await saveDriverTags({
          driverTagBulkCreate: { tags: driverTags },
        }).unwrap();
        closeModal();
      } catch (error) {
        const message = formatServerError(error);
        setError(message);
      }
    },
    [closeModal, saveDriverTags, selectedDriver]
  );

  return (
    <TagModal
      header="Add Driver Tags"
      initialTagIds={selectedDriver?.tags?.map((DriverTag) => DriverTag.tag)}
      modalProps={{
        open: driverTagModal.isOpen,
        setOpen: closeModal,
      }}
      isExternalLoading={isDriverLoading || isDriverFetching}
      externalError={
        DriverError ? 'Failed to load existing driver tags' : error
      }
      handleSaveTags={handleSubmit}
    />
  );
}

export const selectOptionsToDriverTags = (
  selectedTags: SelectOption[],
  tags: TagRead[],
  selectedDriver?: DriverRead
): DriverTagRead[] => {
  return selectedTags.map((selectedTag) => {
    const driverTag = tags.find((t) => t.id === selectedTag.value);
    if (!driverTag) {
      throw new Error(`Tag ${selectedTag.value} not found`);
    }

    if (!selectedDriver) {
      throw new Error('Driver not found');
    }

    return {
      tag_name: driverTag.tag_name,
      color_hex: driverTag.color_hex,
      driver: selectedDriver.id,
      tag: driverTag.id,
    } as DriverTagRead;
  });
};
